export type ProfileImageType = 'avatar' | 'background';

const INPUT_MAX_BYTES = 8 * 1024 * 1024;

const CONFIG = {
  avatar: {
    width: 256,
    height: 256,
    initialQuality: 0.82,
    minQuality: 0.56,
    maxUploadBytes: 300 * 1024,
  },
  background: {
    width: 1280,
    height: 480,
    initialQuality: 0.78,
    minQuality: 0.54,
    maxUploadBytes: 1.5 * 1024 * 1024,
  },
} satisfies Record<ProfileImageType, {
  width: number;
  height: number;
  initialQuality: number;
  minQuality: number;
  maxUploadBytes: number;
}>;

function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Could not encode image.'));
          return;
        }
        resolve(blob);
      },
      'image/webp',
      quality
    );
  });
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const url = URL.createObjectURL(file);
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read that image.'));
    };
    image.src = url;
  });
}

export function validateProfileImageFile(file: File): void {
  if (!file.type.startsWith('image/')) {
    throw new Error('Choose an image file.');
  }
  if (file.size > INPUT_MAX_BYTES) {
    throw new Error('Choose an image under 8 MB.');
  }
}

export async function resizeProfileImage(file: File, type: ProfileImageType): Promise<Blob> {
  validateProfileImageFile(file);

  const config = CONFIG[type];
  const image = await loadImage(file);
  const canvas = document.createElement('canvas');
  canvas.width = config.width;
  canvas.height = config.height;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Image resizing is not supported in this browser.');

  const sourceRatio = image.naturalWidth / image.naturalHeight;
  const targetRatio = config.width / config.height;
  let sourceWidth = image.naturalWidth;
  let sourceHeight = image.naturalHeight;
  let sourceX = 0;
  let sourceY = 0;

  if (sourceRatio > targetRatio) {
    sourceWidth = image.naturalHeight * targetRatio;
    sourceX = (image.naturalWidth - sourceWidth) / 2;
  } else {
    sourceHeight = image.naturalWidth / targetRatio;
    sourceY = (image.naturalHeight - sourceHeight) / 2;
  }

  ctx.drawImage(
    image,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    config.width,
    config.height
  );

  for (let quality = config.initialQuality; quality >= config.minQuality; quality -= 0.08) {
    const blob = await canvasToBlob(canvas, quality);
    if (blob.size <= config.maxUploadBytes) return blob;
  }

  throw new Error('Image is too detailed. Try a simpler or smaller image.');
}

export async function uploadProfileImage(opts: {
  type: ProfileImageType;
  blob: Blob;
  idToken: string;
  oldUrl?: string | null;
}): Promise<string> {
  const params = new URLSearchParams({ type: opts.type });
  if (opts.oldUrl) params.set('oldUrl', opts.oldUrl);

  const response = await fetch(`/api/profile?${params.toString()}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${opts.idToken}`,
      'Content-Type': opts.blob.type || 'image/webp',
    },
    body: opts.blob,
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(typeof payload.error === 'string' ? payload.error : 'Failed to upload image.');
  }
  if (typeof payload.url !== 'string') {
    throw new Error('Upload did not return an image URL.');
  }
  return payload.url;
}

export async function deleteProfileImage(opts: {
  type: ProfileImageType;
  idToken: string;
  url?: string | null;
}): Promise<void> {
  if (!opts.url) return;
  const params = new URLSearchParams({ type: opts.type, url: opts.url });
  const response = await fetch(`/api/profile?${params.toString()}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${opts.idToken}`,
    },
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(typeof payload.error === 'string' ? payload.error : 'Failed to delete image.');
  }
}
